import React, { FocusEvent, useEffect, useRef, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import {
    faCircleCheck,
    faCreditCard,
    faEye,
    faPenToSquare,
    faTrashCan,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import {
    ApprovalState,
    MarketplaceParty,
    RemovalProposalStatus,
    ThirdPartyPayoff,
    ThirdPartyPayoffDocumentKind,
    VendorContractColumn,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { getPartyName } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EM_DASH, formatDate, formatMoneyExact } from '../../../utils/flowOfFundsFormat';
import { ApprovalControl } from '../ApprovalControl/ApprovalControl';
import { DaqiCellEditor } from './DaqiCellEditor';
import { PayoffTableDatePicker } from './PayoffTableDatePicker';
import { SupportingDocumentCell } from './SupportingDocumentCell';
import { ThirdPartyPayoffsAutosave } from './useThirdPartyPayoffsAutosave';

const PartyApprovalCell = ({
    party,
    approval,
    tableRowId,
    canApprove,
    viewerParty,
    onApprovalChange,
}: {
    party: MarketplaceParty;
    approval: ApprovalState;
    tableRowId: number;
    canApprove: boolean;
    viewerParty: MarketplaceParty | null;
    onApprovalChange: (tableRowId: number, isApproved: boolean | null) => void;
}) => {
    const label = party === MarketplaceParty.Buyer ? 'Buyer' : 'Seller';

    if (viewerParty !== party || !canApprove) {
        return (
            <ApprovalControl
                otherPartyApproval={approval}
                otherPartyLabel={label}
                approval={approval}
                viewerLabel={label}
                onChange={() => {}}
                renderOnly="otherParty"
            />
        );
    }

    const toIsApproved = (next: ApprovalState): boolean | null =>
        next === ApprovalState.Approved ? true : next === ApprovalState.Rejected ? false : null;

    return (
        <ApprovalControl
            otherPartyApproval={approval}
            otherPartyLabel={label}
            approval={approval}
            viewerLabel={label}
            onChange={(next) => onApprovalChange(tableRowId, toIsApproved(next))}
            renderOnly="own"
        />
    );
};

const ISO_DATE_LENGTH = 10;

const toDate = (isoDate: string) => {
    if (!isoDate) {
        return null;
    }
    const parsed = new Date(`${isoDate}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDate = (date: Date | null) => {
    if (!date) {
        return '';
    }
    const offsetMinutes = date.getTimezoneOffset();
    return new Date(date.getTime() - offsetMinutes * 60000)
        .toISOString()
        .slice(0, ISO_DATE_LENGTH);
};

const PayoffDateCell = ({
    payoffDate,
    isBlank,
    isEditable,
    isRowDirty,
    onCommit,
}: {
    payoffDate: string | null;
    isEditable: boolean;
    isRowDirty: boolean;
    onCommit: (nextValue: string | null) => void;
    isBlank: boolean;
}) => {
    const [date, setDate] = useState(payoffDate ?? '');

    useEffect(() => {
        if (!isRowDirty) {
            setDate(payoffDate ?? '');
        }
    }, [payoffDate, isRowDirty]);

    if (isBlank) {
        return <td />;
    }

    if (!isEditable) {
        return (
            <td>
                <span>{formatDate(payoffDate)}</span>
            </td>
        );
    }

    return (
        <td>
            <PayoffTableDatePicker
                label="Pay-Off Date"
                selected={toDate(date)}
                onChange={(value) => {
                    const nextDate = toIsoDate(value);
                    setDate(nextDate);
                    onCommit(nextDate || null);
                }}
            />
        </td>
    );
};

const PerDiemCell = ({
    perDiem,
    isBlank,
    isEditable,
    isRowDirty,
    onCommit,
}: {
    perDiem: number | null;
    isEditable: boolean;
    isRowDirty: boolean;
    onCommit: (nextValue: number | null) => void;
    isBlank: boolean;
}) => {
    const [diem, setDiem] = useState(perDiem != null ? String(perDiem) : '');

    useEffect(() => {
        if (!isRowDirty) {
            setDiem(perDiem != null ? String(perDiem) : '');
        }
    }, [perDiem, isRowDirty]);

    if (isBlank) {
        return <td />;
    }

    if (!isEditable) {
        return (
            <td>
                <span>{formatMoneyExact(perDiem)}</span>
            </td>
        );
    }

    return (
        <td>
            <CurrencyInput
                className={`form-control${diem.trim() === '' ? ' is-invalid' : ''}`}
                value={diem}
                allowDecimals
                decimalsLimit={2}
                allowNegativeValue={false}
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                onValueChange={(nextValue) => setDiem(nextValue ?? '')}
                onBlur={() => onCommit(diem.trim() === '' ? null : Number(diem))}
            />
        </td>
    );
};

const PayoffAmountCell = ({
    amount,
    isBlank,
    isEditable,
    onCommit,
}: {
    amount: number | null;
    isEditable: boolean;
    onCommit: (nextValue: number | null) => void;
    isBlank: boolean;
}) => {
    const [value, setValue] = useState(amount != null ? String(amount) : '');
    const isEditingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setValue(amount != null ? String(amount) : '');
        }
    }, [amount]);

    if (isBlank) {
        return <td />;
    }

    if (!isEditable) {
        return (
            <td>
                <span>{formatMoneyExact(amount)}</span>
            </td>
        );
    }

    return (
        <td>
            <CurrencyInput
                className={`form-control${value.trim() === '' ? ' is-invalid' : ''}`}
                value={value}
                allowDecimals
                decimalsLimit={2}
                allowNegativeValue={false}
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                onValueChange={(nextValue) => {
                    isEditingRef.current = true;
                    setValue(nextValue ?? '');
                }}
                onBlur={() => {
                    isEditingRef.current = false;
                    const nextAmount = value.trim() === '' ? null : Number(value);
                    if (nextAmount !== amount) {
                        onCommit(nextAmount);
                    }
                }}
            />
        </td>
    );
};

export interface ThirdPartyPayoffRowProps {
    row: ThirdPartyPayoff;
    columns: VendorContractColumn[];
    fileColumnId: number | null;
    viewerParty: MarketplaceParty | null;
    showRemovalColumn: boolean;
    autosave: ThirdPartyPayoffsAutosave;
    onOpenWireInstructions: (tableRowId: number) => void;
    onSupportingDocumentUpload: (
        tableRowId: number,
        documentKind: ThirdPartyPayoffDocumentKind,
        file: File
    ) => void;
    onSupportingDocumentDelete: (
        tableRowId: number,
        documentKind: ThirdPartyPayoffDocumentKind
    ) => void;
    onSupportingDocumentDownload: (
        tableRowId: number,
        documentKind: ThirdPartyPayoffDocumentKind
    ) => void;
    onApprovalChange: (tableRowId: number, isApproved: boolean | null) => void;
    onBuyerAssumptionChange: (tableRowId: number, isAssumedByBuyer: boolean) => void;
    onPayoffAmountChange: (tableRowId: number, payoffAmount: number | null) => void;
    onOpenContractDocument: (tableRowId: number, fileId: number) => void;
    onOpenRemovalProposalModal: (tableRowId: number) => void;
    onRespondToRemoval: (tableRowId: number, isApproved: boolean) => void;
    onOpenFilePopup: (tableRowId: number, title: string) => void;
}

export const ThirdPartyPayoffRow = ({
    row,
    columns,
    fileColumnId,
    viewerParty,
    showRemovalColumn,
    autosave,
    onOpenWireInstructions,
    onSupportingDocumentUpload,
    onSupportingDocumentDelete,
    onSupportingDocumentDownload,
    onApprovalChange,
    onBuyerAssumptionChange,
    onPayoffAmountChange,
    onOpenContractDocument,
    onOpenRemovalProposalModal,
    onRespondToRemoval,
    onOpenFilePopup,
}: ThirdPartyPayoffRowProps) => {
    const rowRef = useRef<HTMLTableRowElement>(null);
    const removalStatus = row.removalProposal?.status;
    const isRowDirty = autosave.isRowDirty(row.tableRowId);
    const pendingPayoffDate = autosave.pendingPayoffDate(row.tableRowId);
    const pendingPerDiem = autosave.pendingPerDiem(row.tableRowId);
    const canEditWireInstructions =
        row.capabilities.canEditWireInstructions && !row.isAssumedByBuyer;

    const handleRowBlur = (event: FocusEvent<HTMLTableRowElement>) => {
        const nextFocusTarget = event.relatedTarget as Node | null;
        if (!rowRef.current || !nextFocusTarget || !rowRef.current.contains(nextFocusTarget)) {
            autosave.flush();
        }
    };

    const isAssumed = row.isAssumedByBuyer;

    return (
        <tr ref={rowRef} onBlur={handleRowBlur}>
            {columns.map((column) => {
                if (column.columnId === fileColumnId) {
                    const files = row.contractDocuments ?? [];
                    const canEditFiles = row.capabilities.canEditCells;
                    const showViewIcon = !canEditFiles && files.length > 1;
                    return (
                        <td key={column.columnId}>
                            <span className="ff-payoffs__doc-cell">
                                {files.length > 0 ? (
                                    <>
                                        <span
                                            className="ff-payoffs__doc"
                                            role="button"
                                            tabIndex={0}
                                            title="Open document"
                                            onClick={() =>
                                                onOpenContractDocument(
                                                    row.tableRowId,
                                                    files[0].fileId
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onOpenContractDocument(
                                                        row.tableRowId,
                                                        files[0].fileId
                                                    );
                                                }
                                            }}>
                                            <span className="ff-payoffs__doc-name">
                                                {files[0].name}
                                            </span>
                                        </span>
                                        {files.length > 1 && (
                                            <span className="ff-payoffs__doc-more">
                                                +{files.length - 1} more
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    !canEditFiles && <span>{EM_DASH}</span>
                                )}
                                {canEditFiles && (
                                    <button
                                        type="button"
                                        title="Manage documents"
                                        className="ff-payoffs__doc-action"
                                        onClick={() =>
                                            onOpenFilePopup(row.tableRowId, column.name)
                                        }>
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </button>
                                )}
                                {showViewIcon && (
                                    <button
                                        type="button"
                                        title="View documents"
                                        className="ff-payoffs__doc-action"
                                        onClick={() =>
                                            onOpenFilePopup(row.tableRowId, column.name)
                                        }>
                                        <FontAwesomeIcon icon={faEye} />
                                    </button>
                                )}
                            </span>
                        </td>
                    );
                }
                const cell = row.cells.find((c) => c.customColumnId === column.customColumnId);
                return (
                    <td key={column.columnId}>
                        <DaqiCellEditor
                            column={column}
                            cell={cell}
                            isEditable={row.capabilities.canEditCells}
                            isRowDirty={isRowDirty}
                            onCommit={(nextAnswerText) =>
                                autosave.setCellAnswerText(row.tableRowId, column, nextAnswerText)
                            }
                        />
                    </td>
                );
            })}

            <td>
                {row.capabilities.canEditBuyerAssumption ? (
                    <select
                        className="form-control"
                        value={row.isAssumedByBuyer ? 'assumed' : 'notAssumed'}
                        onChange={(e) =>
                            onBuyerAssumptionChange(
                                row.tableRowId,
                                e.target.value === 'assumed'
                            )
                        }>
                        <option value="assumed">Assumed</option>
                        <option value="notAssumed">Not Assumed</option>
                    </select>
                ) : (
                    <span>{row.isAssumedByBuyer ? 'Assumed' : 'Not Assumed'}</span>
                )}
            </td>

            <td>
                {isAssumed ? null : (
                <SupportingDocumentCell
                    uploadedDocument={row.payoffLetterDocument}
                    uploadLabel="Upload Pay-Off Letter"
                    isEditable={row.capabilities.canEditSupportingDocument && !row.isAssumedByBuyer}
                    onUpload={(file) =>
                        onSupportingDocumentUpload(
                            row.tableRowId,
                            ThirdPartyPayoffDocumentKind.PayoffLetter,
                            file
                        )
                    }
                    onDelete={() =>
                        onSupportingDocumentDelete(
                            row.tableRowId,
                            ThirdPartyPayoffDocumentKind.PayoffLetter
                        )
                    }
                    onDownload={() =>
                        onSupportingDocumentDownload(
                            row.tableRowId,
                            ThirdPartyPayoffDocumentKind.PayoffLetter
                        )
                    }
                />
                )}
            </td>

            <PayoffDateCell
                isBlank={isAssumed}
                payoffDate={pendingPayoffDate !== undefined ? pendingPayoffDate : row.payoffDate}
                isEditable={row.capabilities.canEditPayoffDetails && !row.isAssumedByBuyer}
                isRowDirty={isRowDirty}
                onCommit={(nextValue) => autosave.setPayoffDate(row.tableRowId, nextValue)}
            />

            <PayoffAmountCell
                isBlank={isAssumed}
                amount={row.payoffAmount}
                isEditable={row.capabilities.canEditPayoffAmount && !row.isAssumedByBuyer}
                onCommit={(nextValue) => onPayoffAmountChange(row.tableRowId, nextValue)}
            />

            <PerDiemCell
                isBlank={isAssumed}
                perDiem={pendingPerDiem !== undefined ? pendingPerDiem : row.perDiem}
                isEditable={row.capabilities.canEditPerDiem && !row.isAssumedByBuyer}
                isRowDirty={isRowDirty}
                onCommit={(nextValue) => autosave.setPerDiem(row.tableRowId, nextValue)}
            />

            <td>
                {isAssumed ? null : (
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm ff-payoffs__wire-btn"
                    disabled={!row.wireInstructions && !canEditWireInstructions}
                    onClick={() => onOpenWireInstructions(row.tableRowId)}>
                    <FontAwesomeIcon icon={faCreditCard} /> Wire Instructions
                </button>
                )}
            </td>

            <PartyApprovalCell
                party={MarketplaceParty.Buyer}
                approval={row.buyerApproval}
                tableRowId={row.tableRowId}
                canApprove={row.capabilities.canApprove}
                viewerParty={viewerParty}
                onApprovalChange={onApprovalChange}
            />
            <PartyApprovalCell
                party={MarketplaceParty.Seller}
                approval={row.sellerApproval}
                tableRowId={row.tableRowId}
                canApprove={row.capabilities.canApprove}
                viewerParty={viewerParty}
                onApprovalChange={onApprovalChange}
            />

            {showRemovalColumn && (
                <td className="ff-payoffs__removal">
                    {row.capabilities.canProposeRemoval && (
                        <button
                            type="button"
                            title="Propose removal"
                            aria-label="Propose removal"
                            className="ff-payoffs__propose"
                            onClick={() => onOpenRemovalProposalModal(row.tableRowId)}>
                            <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                    )}

                    {row.removalProposal && removalStatus === RemovalProposalStatus.Pending && (
                        <span className="ff-payoffs__removal-pending">
                            <span className="ff-payoffs__removal-note">
                                Proposed by {getPartyName(row.removalProposal.proposedBy)} ·{' '}
                                Awaiting the other party
                                <InfoTooltip text={row.removalProposal.comment} />
                            </span>
                            {row.capabilities.canRespondToRemoval && (
                                <span className="ff-payoffs__removal-actions">
                                    <button
                                        type="button"
                                        title="Approve removal"
                                        className="ff-approval__btn ff-approval__btn--yes"
                                        onClick={() => onRespondToRemoval(row.tableRowId, true)}>
                                        <FontAwesomeIcon icon={faCircleCheck} />
                                    </button>
                                    <button
                                        type="button"
                                        title="Reject removal"
                                        className="ff-approval__btn ff-approval__btn--no"
                                        onClick={() => onRespondToRemoval(row.tableRowId, false)}>
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </span>
                            )}
                        </span>
                    )}
                </td>
            )}
        </tr>
    );
};
